import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchSearchResults } from "../api/GlobalApi";
import { Helmet } from "react-helmet-async";

function Search() {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const [results, setResults] = useState({ apps: [], games: [], blogs: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (query) {
            loadResults();
        }
    }, [query]);

    const loadResults = async () => {
        setLoading(true);
        try {
            const data = await fetchSearchResults(query);
            setResults(data);
        } catch (error) {
            console.error("Error fetching search results:", error);
        } finally {
            setLoading(false);
        }
    };

    const getStars = (rating) => {
        const fullStars = Math.floor(rating);
        return "★".repeat(fullStars) + "☆".repeat(5 - fullStars);
    };

    if (loading) {
        return (
            <div style={styles.loaderContainer}>
                <div style={styles.spinner}></div>
            </div>
        );
    }

    const totalResults = results.apps.length + results.games.length + results.blogs.length;

    return (
        <main className="main-content">
            <Helmet>
                <title>Search results for "{query}" - Play Store</title>
            </Helmet>

            <div className="breadcrumb">
                <Link to='/'>Home</Link>
                <span>›</span>
                <span>Search results for "{query}"</span>
            </div>

            <section className="search-results-header">
                <h1>Search Results</h1>
                <p>{totalResults} results found for "{query}"</p>
            </section>

            {/* Apps Section */}
            {results.apps.length > 0 && (
                <section className="similar-app">
                    <h2 className="section-title">Apps ({results.apps.length})</h2>
                    <div className="apps-grid">
                        {results.apps.map((app) => {
                            const rating = parseFloat(app.rating) || 0;
                            const fullStars = Math.floor(rating);

                            return (
                                <Link 
                                    to={`/app/details/${app.slug}`} 
                                    key={app.id} 
                                    className="app-card"
                                    style={{ textDecoration: "none" }}
                                >
                                    <div className="app-card-content">
                                        <div
                                            className="app-card-icon"
                                            style={{
                                                backgroundImage: `url(${app.icon_url})`,
                                                backgroundSize: "cover",
                                                backgroundPosition: "center",
                                                borderRadius: "12px",
                                                width: "60px",
                                                height: "60px"
                                            }}
                                        ></div>
                                        <div className="app-card-info">
                                            <div className="app-card-title">{app.name}</div>
                                            <div className="app-card-developer">{app.developer}</div>
                                            <div className="app-card-rating">
                                                <span className="app-card-stars">
                                                    {"★".repeat(fullStars) + "☆".repeat(5 - fullStars)}
                                                </span>
                                                <span className="app-card-rating-text">{rating.toFixed(1)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Games Section */}
            {results.games.length > 0 && (
                <section className="similar-app">
                    <h2 className="section-title">Games ({results.games.length})</h2>
                    <div className="apps-grid">
                        {results.games.map((game) => {
                            const rating = parseFloat(game.rating) || 0;
                            const fullStars = Math.floor(rating);

                            return (
                                <Link 
                                    to={`/app/details/${game.slug}`} 
                                    key={game.id} 
                                    className="app-card"
                                    style={{ textDecoration: "none" }}
                                >
                                    <div className="app-card-content">
                                        <div
                                            className="app-card-icon"
                                            style={{
                                                backgroundImage: `url(${game.icon_url})`,
                                                backgroundSize: "cover",
                                                backgroundPosition: "center",
                                                borderRadius: "12px",
                                                width: "60px",
                                                height: "60px"
                                            }}
                                        ></div>
                                        <div className="app-card-info">
                                            <div className="app-card-title">{game.name}</div>
                                            <div className="app-card-developer">{game.developer}</div>
                                            <div className="app-card-rating">
                                                <span className="app-card-stars">
                                                    {"★".repeat(fullStars) + "☆".repeat(5 - fullStars)}
                                                </span>
                                                <span className="app-card-rating-text">{rating.toFixed(1)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Blogs Section */}
            {results.blogs.length > 0 && (
                <section className="similar-app">
                    <h2 className="section-title">Blog Posts ({results.blogs.length})</h2>
                    <div className="blogs-grid" style={{ 
                        display: "grid", 
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", 
                        gap: "20px" 
                    }}>
                        {results.blogs.map((blog) => (
                            <Link 
                                to={`/blog/${blog.slug}`} 
                                key={blog.id}
                                style={{ textDecoration: "none" }}
                            >
                                <div style={{
                                    background: "white",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    transition: "transform 0.2s"
                                }}>
                                    {blog.featured_image && (
                                        <img 
                                            src={blog.featured_image_url} 
                                            alt={blog.title}
                                            style={{
                                                width: "100%",
                                                height: "180px",
                                                objectFit: "cover"
                                            }}
                                        />
                                    )}
                                    <div style={{ padding: "16px" }}>
                                        <h3 style={{ 
                                            fontSize: "16px", 
                                            marginBottom: "8px",
                                            color: "#202124"
                                        }}>
                                            {blog.title}
                                        </h3>
                                        {blog.excerpt && (
                                            <p style={{ 
                                                fontSize: "14px", 
                                                color: "#5f6368",
                                                lineHeight: "1.5"
                                            }}>
                                                {blog.excerpt.substring(0, 100)}...
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* No Results */}
            {totalResults === 0 && (
                <section style={{ textAlign: "center", padding: "60px 20px" }}>
                    <h2>No results found</h2>
                    <p style={{ color: "#5f6368", marginTop: "10px" }}>
                        Try searching with different keywords
                    </p>
                </section>
            )}
        </main>
    );
}

const styles = {
    loaderContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
    },
    spinner: {
        width: "50px",
        height: "50px",
        border: "6px solid #ddd",
        borderTop: "6px solid #3498db",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
    },
};

export default Search;